// Vendor Libs
import React, {Component} from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {get, isEmpty, map} from 'lodash';

// Components
import Checkbox from '../core/checkbox';
import Input from '../core/input';
import LanguageSelect from '../app/language-select';
import LockedIndicator from '../app/locked-indicator';
import MassActionTable from './mass-action-table';
import MultiSelect from '../core/multi-select';
import NetworkSelect from '../app/network-select';
import ProviderSelect from '../app/provider-select';
import Select from '../core/select';
import ServiceSelect from '../app/service-select';
import ServicesExportPanelAction from '../app/services-export-panel-action';
import ServicesSearchForm from '../app/services-search-form';
import ServicesSearchPanelAction from '../app/services-search-panel-action';
import TableWithSort from '../core/table-with-sort';

// Containers
import withSearch from '../../containers/with-search';

// Styles
import './unmapped-media-items.scss';

const servicesDataKey = 'unmappedMediaItems';

class UnmappedMediaItems extends Component {
    priorityOptions = ['IMMEDIATE', 'URGENT', 'HIGH', 'NORMAL', 'LOW'];

    getColumnConfig() {
        const {detailLink} = this.props;

        return [
            {
                dataKey: '',
                type: 'component',
                component: LockedIndicator,
                componentProps: this.getLockedIndicatorProps,
                width: '3%',
            },
            {
                className: 'table-with-sort--word-break',
                dataKey: 'title',
                formatter: 'lockedLink',
                link: detailLink || '/media-items',
                linkKey: 'guid',
                massActionWidth: '25%',
                sortKey: 'title.sortable',
                title: 'Title',
                type: 'link',
                width: '27%',
            },
            {
                dataKey: 'service.serviceName',
                title: 'Service',
                width: '10%',
            },
            {
                dataKey: 'externalNetwork.name',
                title: 'Network',
                width: '16%',
            },
            {
                className: 'table-with-sort--word-break',
                dataKey: 'showName',
                massActionWidth: '26%',
                sortKey: 'showName.sortable',
                title: 'Show Name',
                width: '27%',
            },
            {
                dataKey: 'priority',
                sortKey: 'sortablePriority',
                title: 'Priority',
                width: '7%',
            },
            {
                dataKey: 'offerings.0.startDate',
                sortKey: 'offerings.startDate',
                title: 'Start Date',
                tooltip: 'Start Date',
                type: 'date',
                width: '10%',
            },
        ];
    }

    getFilteredTablewithsortProps() {
        return {
            columnConfig: this.getColumnConfig(),
            rowDetails: this.getRowDetails(),
            servicesDataKey,
            ...this.props,
            localSort: true,
            resetSort: null,
            setSort: null,
        };
    }

    getLockedIndicatorProps = ({guid}) => ({
        className: 'media-items--lock',
        lockId: guid,
        serviceKey: 'mediaItems',
    });

    getMassActionOptions() {
        return [
            {
                formName: 'bulkMapSpecials',
                label: 'Bulk Map Specials',
                showMessage: false,
            },
            {
                formName: 'bulkMapShows',
                label: 'Bulk Map Shows',
                showMessage: false,
            },
        ];
    }

    getSearchExportPanelProps = (searchData) => {
        const {totalElements, query} = this.props;
        return {
            totalElements,
            query,
            searchData,
        };
    };

    getPaginationProps() {
        const {query, totalPages} = this.props;

        return {
            maxResults: 10000,
            maxPages: 9,
            query,
            totalPages,
        };
    }

    getPriorityMultiSelectProps() {
        return {
            multi: true,
            operator: '=in=',
            placeholder: 'Filter by Priority',
            options: map(this.priorityOptions, (value) => ({value, label: value})),
        };
    }

    getResponseTablewithsortProps() {
        return {
            columnConfig: [
                {
                    dataKey: 'target',
                    title: 'Guid',
                    link: '/media-items',
                    linkKey: 'target',
                    type: 'link',
                    width: '35%',
                },
                {
                    dataKey: 'statusPhrase',
                    title: 'Status',
                    width: '15%',
                },
                {
                    dataKey: 'description',
                    title: 'Title',
                    width: '50%',
                },
            ],
        };
    }

    getRowDetails() {
        return {
            rowEmphasis: (dataRow) => {
                const {sortablePriority} = dataRow;

                return sortablePriority === 1 || sortablePriority === 2;
            },
            whitelistKeys: [
                'description',
                'releaseYear',
                'originalAirDate',
                'seasonNumber',
                'episodeNumber',
                'duration',
                'rating',
                'lang',
                'genres',
            ],
        };
    }

    getTablewithsortProps() {
        return {
            columnConfig: this.getColumnConfig(),
            formName: servicesDataKey,
            rowDetails: this.getRowDetails(),
            servicesDataKey,
            ...this.props,
        };
    }

    hasTargets() {
        const {searchFilter} = this.props;
        return !isEmpty(get(searchFilter, 'targets'));
    }

    renderFilteredTablewithsort() {
        return this.hasTargets() ? <TableWithSort {...this.getFilteredTablewithsortProps()} /> : null;
    }

    renderForm = () => (
        <ServicesSearchForm advancedSearch={true} formName={servicesDataKey} servicesDataKey={servicesDataKey}>
            <Input label="Title/Show Name" name="title||showName" />
            <Checkbox label="Exact Match" name="exactSearch" value="true" />
            <Checkbox label="Fast Path" name="externalNetwork.network.fastPath" value="true" />
            <Checkbox
                label="Cross Service Mapping"
                name="externalNetwork.network.crossServiceMappingEnabled"
                value="true"
            />
            <Select label="Duration" name="duration">
                <option value="">Filter by Duration</option>
                <option value="<=420">&lt;= 7 minutes</option>
                <option value="<=900">&lt;= 15 minutes</option>
                <option value=">900">&gt; 15 minutes</option>
            </Select>
            <LanguageSelect defaultOption="Filter by Language" label="Language" name="lang" />
            <ServiceSelect label="Services" name="service.serviceName" />
            <Input label="ID from Partner" name="idFromPartner" />
            <NetworkSelect label="Network" name="externalNetwork.name||externalNetwork.network.name" />
            <Input label="External Network" name="externalNetwork.name" />
            <ProviderSelect label="Provider" name="provider.partnerId" />
            <Input label="GUID" name="guid||externalGuid" />
            <Input label="RUID" name="offerings.resourceId" />
            <Select label="Clip" name="expression">
                <option value="">Filter by Clip</option>
                <option value="clip">Show clips only</option>
                <option value="!clip">Hide all clips</option>
            </Select>
            <MultiSelect label="Priority" name="priority" {...this.getPriorityMultiSelectProps()} />
        </ServicesSearchForm>
    );

    render() {
        const {searchData} = this.props;
        const tableWithSortClassName = classnames(
            {
                'unmapped-media-items--search-table_hide': this.hasTargets(),
            },
            'unmapped-media-items--search-table'
        );

        return (
            <div className="unmapped-media-items">
                <div className={tableWithSortClassName}>
                    <ServicesExportPanelAction
                        exportConfig="mediaItems"
                        {...this.getSearchExportPanelProps(searchData)}
                    />
                    <ServicesSearchPanelAction contentRenderer={this.renderForm} />
                    <MassActionTable
                        actionIdKey="guid"
                        massActionName={servicesDataKey}
                        massActionOptions={this.getMassActionOptions()}
                        paginationProps={this.getPaginationProps()}
                        responseTablewithsortProps={this.getResponseTablewithsortProps()}
                        serviceKey="mediaItems"
                        showMassActionsPanelAction={!this.hasTargets()}
                        tableWithSortProps={this.getTablewithsortProps()}
                    />
                </div>
                {this.renderFilteredTablewithsort()}
            </div>
        );
    }
}

UnmappedMediaItems.propTypes = {
    activeRowKeys: PropTypes.array,
    detailLink: PropTypes.string,
    query: PropTypes.object,
    routeParams: PropTypes.object,
    searchData: PropTypes.array,
    searchFilter: PropTypes.object,
    totalElements: PropTypes.number,
    totalPages: PropTypes.number,
};

export default withSearch(UnmappedMediaItems, servicesDataKey);
